/**
 * PartnerPreviewPage.jsx
 * One link for the email. The visitor lands, sees a sample partner card, clicks
 * "Upload your logo", and the card instantly shows their logo in the same design
 * the live Partner Network uses (shared PartnerCard.jsx).
 *
 * The uploaded logo is a local, in-browser copy only (object URL). It is not sent
 * to the server and not shown on the website unless they submit a partnership request.
 *
 * Email link:
 *   https://pilotlinkusa.org/partner-preview?invite=<token>&company=ABC%20Insurance&category=insurance
 *
 * Partnership request: multipart POST to `apiUrl`
 *   contact_name, company_name, email, phone, website, category, offer,
 *   authorized ("true"), invite_token, logo (file, if uploaded)
 *   201 = success; 400 = { field: ["message"] } shown under each field.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { T, CATEGORIES, PartnerCard, useBrandFonts, getCategory } from "./PartnerCard";

const ACCEPTED_TYPES = ["image/png", "image/svg+xml", "image/jpeg", "image/webp"];
const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const CONTACT_EMAIL = "jordan.lee@pilotlinkusa.org";

const BENEFITS = [
  {
    title: "A listing on our Partner Network page",
    text: "Your logo, category, and a link to your website on the page PilotLink members use to find industry services.",
  },
  {
    title: "Found by the right people",
    text: "Pilot car operators, carriers, and dispatchers who use PilotLink USA to coordinate oversize loads.",
  },
  {
    title: "Listed in your category",
    text: "Members looking for insurance, equipment, training, permits, or transportation services see partners in that category.",
  },
  {
    title: "Partner promotions",
    text: "Inclusion in partner announcements and promotions shared with PilotLink members.",
  },
  {
    title: "Results you can track",
    text: "Tracked referral links, so you can see the visits and leads that come from PilotLink USA.",
  },
  {
    title: "Terms that fit your team",
    text: "A short paid pilot, an affiliate or revenue-share arrangement, or a mix of both.",
  },
];

const HOW_IT_WORKS = [
  "Preview your logo on this page",
  "Request a partnership",
  "We set up your listing and tracked link",
  "You approve it before it goes live",
];

// ---------- Helpers ----------
function getCookie(name) {
  const match = document.cookie.match(new RegExp("(^|;\\s*)" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[2]) : null;
}

function normalizeUrl(value) {
  const v = value.trim();
  if (!v) return "";
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
}

function readInviteParams() {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  const category = p.get("category");
  return {
    invite: p.get("invite") || "",
    company: (p.get("company") || "").slice(0, 120),
    category: CATEGORIES.some((c) => c.value === category) ? category : "",
  };
}

// ---------- Small building blocks ----------
function MileMarker({ n }) {
  return (
    <span
      aria-hidden="true"
      className="inline-flex items-center justify-center rounded-[3px] shrink-0"
      style={{
        width: 28,
        height: 36,
        background: T.marker,
        color: T.paper,
        border: `2px solid ${T.paper}`,
        boxShadow: `0 0 0 1.5px ${T.marker}`,
        fontFamily: T.head,
        fontWeight: 700,
        fontSize: 16,
      }}
    >
      {n}
    </span>
  );
}

function Field({ id, label, optional, error, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} style={{ fontWeight: 600, color: T.ink, fontSize: 15 }}>
        {label}
        {optional && <span style={{ color: T.muted, fontWeight: 400 }}> (optional)</span>}
      </label>
      {children}
      {error && (
        <p id={`${id}-error`} role="alert" style={{ color: T.danger, fontSize: 13.5, fontWeight: 500 }}>
          {error}
        </p>
      )}
    </div>
  );
}

const inputStyle = (hasError) => ({
  width: "100%",
  padding: "11px 12px",
  fontSize: 16, // prevents iOS zoom on focus
  color: T.ink,
  background: T.paper,
  border: `1.5px solid ${hasError ? T.danger : T.line}`,
  borderRadius: 6,
  fontFamily: T.body,
});

const headBtn = { fontFamily: T.head, fontWeight: 700, fontSize: 18, letterSpacing: "0.02em" };

// ---------- Page ----------
export default function PartnerPreviewPage({ apiUrl = "/api/partners/requests/" }) {
  useBrandFonts();
  const invite = useMemo(readInviteParams, []);

  const [form, setForm] = useState({
    company_name: invite.company || "",
    category: invite.category || "insurance",
    contact_name: "",
    email: "",
    phone: "",
    website: "",
    offer: "",
    authorized: false,
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoUrl, setLogoUrl] = useState("");
  const [logoError, setLogoError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [justUploaded, setJustUploaded] = useState(0);

  const [showRequest, setShowRequest] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const fileInputRef = useRef(null);
  const requestRef = useRef(null);

  useEffect(() => () => logoUrl && URL.revokeObjectURL(logoUrl), [logoUrl]);

  useEffect(() => {
    if (showRequest) requestRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [showRequest]);

  const set = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((er) => ({ ...er, [key]: undefined }));
  };

  function acceptLogo(file) {
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setLogoError("Use a PNG, SVG, JPG, or WebP file.");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setLogoError("Logo must be 2 MB or smaller.");
      return;
    }
    setLogoError("");
    setLogoFile(file);
    setLogoUrl(URL.createObjectURL(file));
    setJustUploaded((n) => n + 1); // retriggers the reveal animation
  }

  function removeLogo() {
    setLogoFile(null);
    setLogoUrl("");
  }

  const openPicker = () => fileInputRef.current?.click();

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitError("");
    const next = {};
    if (!form.contact_name.trim()) next.contact_name = "Enter a contact name.";
    if (!form.company_name.trim()) next.company_name = "Enter your company name.";
    if (!EMAIL_RE.test(form.email.trim())) next.email = "Enter a valid business email.";
    if (!form.website.trim()) next.website = "Enter your company website.";
    if (!form.offer.trim()) next.offer = "Tell us what you'd like to offer PilotLink members.";
    if (!form.authorized) next.authorized = "Confirm you're authorized to represent this company.";
    setErrors(next);
    if (Object.keys(next).length) return;

    const fd = new FormData();
    fd.append("contact_name", form.contact_name.trim());
    fd.append("company_name", form.company_name.trim());
    fd.append("email", form.email.trim());
    fd.append("phone", form.phone.trim());
    fd.append("website", normalizeUrl(form.website));
    fd.append("category", form.category);
    fd.append("offer", form.offer.trim());
    fd.append("authorized", "true");
    fd.append("invite_token", invite.invite || "");
    if (logoFile) fd.append("logo", logoFile);

    const headers = {};
    const csrf = getCookie("csrftoken");
    if (csrf) headers["X-CSRFToken"] = csrf;

    setSubmitting(true);
    try {
      const res = await fetch(apiUrl, { method: "POST", body: fd, headers, credentials: "include" });
      if (res.ok) {
        setSubmitted(true);
        return;
      }
      let data = null;
      try {
        data = await res.json();
      } catch {
        /* non-JSON body */
      }
      if (res.status === 400 && data && typeof data === "object") {
        const fieldErrors = {};
        Object.entries(data).forEach(([k, v]) => (fieldErrors[k] = Array.isArray(v) ? v.join(" ") : String(v)));
        setErrors(fieldErrors);
        setSubmitError(
          fieldErrors.non_field_errors || fieldErrors.detail || fieldErrors.logo || "Some fields need attention."
        );
      } else if (res.status === 429) {
        setSubmitError("Too many requests from this connection. Wait a few minutes and submit again.");
      } else {
        setSubmitError(`The request didn't go through (error ${res.status}). Try again, or email ${CONTACT_EMAIL}.`);
      }
    } catch {
      setSubmitError("Couldn't reach PilotLink USA. Check your connection and submit again.");
    } finally {
      setSubmitting(false);
    }
  }

  const cat = getCategory(form.category);

  return (
    <div className="pl-root" style={{ background: T.mist, minHeight: "100vh", fontFamily: T.body, color: T.ink }}>
      <style>{`
        .pl-root .pl-focus:focus-visible, .pl-root input:focus-visible, .pl-root select:focus-visible,
        .pl-root textarea:focus-visible, .pl-root button:focus-visible { outline: 3px solid ${T.amber}; outline-offset: 2px; }
        .pl-reveal { animation: plReveal 420ms cubic-bezier(.2,.7,.2,1) both; }
        @keyframes plReveal { from { opacity: 0; transform: translateY(8px) scale(.985); } to { opacity: 1; transform: none; } }
        @media (prefers-reduced-motion: reduce) { .pl-reveal { animation: none; } }
      `}</style>

      {/* Header */}
      <header style={{ background: T.navy, borderBottom: `4px solid ${T.amber}` }}>
        <div className="mx-auto max-w-6xl px-5 py-4">
          <a href="https://pilotlinkusa.org" className="pl-focus" style={{ color: T.paper, textDecoration: "none" }}>
            <span style={{ fontFamily: T.head, fontWeight: 700, fontSize: 22 }}>PilotLink USA</span>
            <span style={{ color: "#B9C6D6", fontSize: 15, marginLeft: 10 }}>Partner Network</span>
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-8 sm:py-12">
        {/* ---------- Hero: upload + live preview ---------- */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-8 lg:gap-12 items-start">
          <section>
            <h1
              style={{
                fontFamily: T.head,
                fontWeight: 700,
                fontSize: "clamp(34px, 5vw, 50px)",
                lineHeight: 1.03,
                color: T.navy,
              }}
            >
              See your brand on PilotLink USA
            </h1>
            <p style={{ fontSize: 18, lineHeight: 1.55, color: T.muted, marginTop: 12, maxWidth: 520 }}>
              Upload your logo to see exactly how {form.company_name.trim() || "your company"} would appear in our
              Partner Network. It takes a few seconds, and nothing is published.
            </p>

            <div className="flex flex-col gap-4 mt-7" style={{ maxWidth: 460 }}>
              <Field id="company_name_preview" label="Company name">
                <input
                  id="company_name_preview"
                  type="text"
                  autoComplete="organization"
                  maxLength={120}
                  value={form.company_name}
                  onChange={set("company_name")}
                  placeholder="Your company name"
                  style={inputStyle(false)}
                />
              </Field>
              <Field id="category_preview" label="Partner category">
                <select
                  id="category_preview"
                  value={form.category}
                  onChange={set("category")}
                  style={inputStyle(false)}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </Field>

              <div className="flex flex-wrap items-center gap-3 mt-1">
                <button
                  type="button"
                  onClick={openPicker}
                  className="rounded-md px-6 py-3.5"
                  style={{ ...headBtn, fontSize: 20, background: T.amber, color: T.amberInk }}
                >
                  {logoFile ? "Upload a different logo" : "Upload your logo"}
                </button>
                {logoFile && (
                  <button
                    type="button"
                    onClick={removeLogo}
                    className="rounded-md px-3 py-2"
                    style={{ color: T.muted, fontSize: 15, textDecoration: "underline" }}
                  >
                    Show sample again
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_TYPES.join(",")}
                className="sr-only"
                aria-label="Upload your logo"
                onChange={(e) => {
                  acceptLogo(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
              <p style={{ fontSize: 13.5, color: logoError ? T.danger : T.muted }} role={logoError ? "alert" : undefined}>
                {logoError || "PNG or SVG with a transparent background looks best. Max 2 MB."}
              </p>
            </div>
          </section>

          {/* Preview of the live page */}
          <section aria-label="Partner listing preview" className="lg:sticky lg:top-6">
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                acceptLogo(e.dataTransfer.files?.[0]);
              }}
              className="rounded-xl overflow-hidden"
              style={{
                border: `${dragOver ? 2 : 1}px ${dragOver ? "dashed" : "solid"} ${dragOver ? T.navy : T.line}`,
                background: T.paper,
                boxShadow: "0 8px 30px rgba(0,35,73,0.10)",
              }}
            >
              <div
                className="flex items-center justify-between gap-2 px-4 py-2.5"
                style={{ background: T.mist, borderBottom: `1px solid ${T.line}`, fontSize: 13, color: T.muted }}
              >
                <span>
                  <span style={{ fontWeight: 600, color: T.ink }}>pilotlinkusa.org</span>/partners
                </span>
                <span
                  className="rounded px-2 py-0.5"
                  style={{
                    background: T.amber,
                    color: T.amberInk,
                    fontFamily: T.head,
                    fontWeight: 700,
                    fontSize: 13,
                    letterSpacing: "0.06em",
                  }}
                >
                  PARTNER PREVIEW
                </span>
              </div>

              <div className="p-5 sm:p-6">
                <h2 style={{ fontFamily: T.head, fontWeight: 700, fontSize: 26, color: T.navy }}>Partner Network</h2>
                <p style={{ color: T.muted, fontSize: 15, marginTop: 2 }}>
                  Companies serving pilot car operators, carriers, and dispatchers.
                </p>

                <div className="flex flex-wrap gap-2 mt-4" aria-hidden="true">
                  {CATEGORIES.map((c) => {
                    const on = c.value === form.category;
                    return (
                      <span
                        key={c.value}
                        className="rounded-full px-3 py-1"
                        style={{
                          fontSize: 13,
                          background: on ? T.navy : T.paper,
                          color: on ? T.paper : T.muted,
                          border: `1px solid ${on ? T.navy : T.line}`,
                        }}
                      >
                        {c.label}
                      </span>
                    );
                  })}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
                  <div key={justUploaded} className={justUploaded ? "pl-reveal" : undefined}>
                    <PartnerCard
                      preview
                      logoUrl={logoUrl}
                      name={form.company_name.trim()}
                      category={form.category}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={openPicker}
                    className="hidden sm:flex flex-col items-center justify-center gap-1 rounded-lg px-4 text-center"
                    style={{ border: `2px dashed ${T.line}`, minHeight: 250, color: T.faint, fontSize: 14 }}
                  >
                    <span style={{ fontWeight: 600, color: T.muted }}>
                      {logoFile ? "Try another version" : "Drop your logo here"}
                    </span>
                    <span>or click to upload</span>
                  </button>
                </div>

                <p className="mt-4" style={{ color: T.muted, fontSize: 13.5, lineHeight: 1.5 }} aria-live="polite">
                  {logoFile
                    ? `This is your logo in the same ${cat.partnerLabel.toLowerCase()} listing design used on our website. `
                    : "Showing a sample logo. Upload yours to see it in place. "}
                  This preview is visible only to you. Your logo stays in this browser and is not published.
                </p>
              </div>
            </div>
          </section>
        </div>

        {/* ---------- Benefits ---------- */}
        <section className="mt-16 sm:mt-20" aria-labelledby="benefits-heading">
          <h2
            id="benefits-heading"
            style={{ fontFamily: T.head, fontWeight: 700, fontSize: "clamp(28px, 4vw, 38px)", color: T.navy }}
          >
            Why partner with PilotLink USA
          </h2>
          <p style={{ color: T.muted, fontSize: 17, lineHeight: 1.55, marginTop: 6, maxWidth: 640 }}>
            We feature a limited number of companies that genuinely serve the oversize transportation industry, so
            members can trust the partners they find here. That trust is what helps both of our businesses grow.
          </p>

          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6 mt-8">
            {BENEFITS.map((b) => (
              <li key={b.title} className="flex gap-4">
                <span
                  aria-hidden="true"
                  className="shrink-0 mt-1.5"
                  style={{ width: 12, height: 12, background: T.amber, transform: "rotate(45deg)" }}
                />
                <div>
                  <h3 style={{ fontWeight: 600, fontSize: 17, color: T.ink }}>{b.title}</h3>
                  <p style={{ color: T.muted, fontSize: 15.5, lineHeight: 1.55, marginTop: 2 }}>{b.text}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* ---------- How it works + CTA ---------- */}
        <section
          className="mt-16 rounded-xl p-6 sm:p-9 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center"
          style={{ background: T.navy, color: T.paper }}
        >
          <div>
            <h2 style={{ fontFamily: T.head, fontWeight: 700, fontSize: "clamp(28px, 4vw, 36px)" }}>
              Like how your company looks?
            </h2>
            <p style={{ color: "#D3DCE7", fontSize: 16.5, lineHeight: 1.55, marginTop: 8, maxWidth: 480 }}>
              Partnering with PilotLink USA introduces your company to carriers, pilot car operators, dispatchers, and
              transportation professionals using our platform.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowRequest(true)}
                className="rounded-md px-6 py-3.5"
                style={{ ...headBtn, background: T.amber, color: T.amberInk }}
              >
                Request Partnership
              </button>
              <button
                type="button"
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                  openPicker();
                }}
                className="rounded-md px-6 py-3.5"
                style={{ ...headBtn, border: `2px solid ${T.paper}`, color: T.paper }}
              >
                Update Logo
              </button>
            </div>
          </div>

          <ol className="flex flex-col gap-4" aria-label="How partnership works">
            {HOW_IT_WORKS.map((s, i) => (
              <li key={s} className="flex items-center gap-4">
                <MileMarker n={i + 1} />
                <span style={{ fontSize: 17, fontWeight: 500 }}>{s}</span>
              </li>
            ))}
          </ol>
        </section>

        {/* ---------- Partnership request ---------- */}
        {showRequest && (
          <section
            ref={requestRef}
            className="mt-10 rounded-xl p-5 sm:p-8 scroll-mt-6"
            style={{ background: T.paper, border: `1px solid ${T.line}` }}
            aria-labelledby="request-heading"
          >
            {submitted ? (
              <div className="flex flex-col gap-3 max-w-2xl" aria-live="polite">
                <h2 id="request-heading" style={{ fontFamily: T.head, fontWeight: 700, fontSize: 30, color: T.navy }}>
                  Partnership request submitted
                </h2>
                <p style={{ fontSize: 16.5, lineHeight: 1.55 }}>
                  Thank you{form.contact_name.trim() ? `, ${form.contact_name.trim().split(" ")[0]}` : ""}. Jordan Lee
                  will review the request for <strong>{form.company_name}</strong> and reply to{" "}
                  <strong>{form.email}</strong>.
                </p>
                <p style={{ fontSize: 15.5, color: T.muted, lineHeight: 1.55 }}>
                  Your listing stays unpublished until the partnership is confirmed and you approve the final version.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
                <div>
                  <h2
                    id="request-heading"
                    style={{ fontFamily: T.head, fontWeight: 700, fontSize: 30, color: T.navy }}
                  >
                    Request partnership
                  </h2>
                  <p style={{ color: T.muted, fontSize: 15.5, marginTop: 4 }}>
                    Jordan Lee, Sales Manager, reviews every request personally.
                    {logoFile ? " The logo you previewed will be included with your request." : ""}
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field id="contact_name" label="Contact name" error={errors.contact_name}>
                    <input
                      id="contact_name"
                      autoComplete="name"
                      value={form.contact_name}
                      onChange={set("contact_name")}
                      aria-invalid={!!errors.contact_name}
                      style={inputStyle(errors.contact_name)}
                    />
                  </Field>
                  <Field id="company_name" label="Company" error={errors.company_name}>
                    <input
                      id="company_name"
                      autoComplete="organization"
                      maxLength={120}
                      value={form.company_name}
                      onChange={set("company_name")}
                      aria-invalid={!!errors.company_name}
                      style={inputStyle(errors.company_name)}
                    />
                  </Field>
                  <Field id="email" label="Business email" error={errors.email}>
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      inputMode="email"
                      value={form.email}
                      onChange={set("email")}
                      aria-invalid={!!errors.email}
                      style={inputStyle(errors.email)}
                    />
                  </Field>
                  <Field id="phone" label="Phone" optional error={errors.phone}>
                    <input
                      id="phone"
                      type="tel"
                      autoComplete="tel"
                      inputMode="tel"
                      value={form.phone}
                      onChange={set("phone")}
                      style={inputStyle(errors.phone)}
                    />
                  </Field>
                  <Field id="website" label="Website" error={errors.website}>
                    <input
                      id="website"
                      type="url"
                      autoComplete="url"
                      inputMode="url"
                      placeholder="yourcompany.com"
                      value={form.website}
                      onChange={set("website")}
                      aria-invalid={!!errors.website}
                      style={inputStyle(errors.website)}
                    />
                  </Field>
                  <Field id="category" label="Partnership category" error={errors.category}>
                    <select id="category" value={form.category} onChange={set("category")} style={inputStyle(errors.category)}>
                      {CATEGORIES.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>

                <Field id="offer" label="What would you like to offer PilotLink members?" error={errors.offer}>
                  <textarea
                    id="offer"
                    rows={4}
                    maxLength={1000}
                    value={form.offer}
                    onChange={set("offer")}
                    placeholder="For example: a member discount, a bundled policy, training credits, priority permit service"
                    aria-invalid={!!errors.offer}
                    style={{ ...inputStyle(errors.offer), resize: "vertical" }}
                  />
                </Field>

                <div className="flex flex-col gap-1.5">
                  <label className="flex items-start gap-3" style={{ fontSize: 15, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={form.authorized}
                      onChange={set("authorized")}
                      aria-invalid={!!errors.authorized}
                      style={{ width: 20, height: 20, marginTop: 2, accentColor: T.navy }}
                    />
                    <span>
                      I'm authorized to represent this company and to share its logo with PilotLink USA for partnership
                      review.
                    </span>
                  </label>
                  {errors.authorized && (
                    <p role="alert" style={{ color: T.danger, fontSize: 13.5, fontWeight: 500 }}>
                      {errors.authorized}
                    </p>
                  )}
                </div>

                {submitError && (
                  <div
                    role="alert"
                    className="rounded-md px-4 py-3"
                    style={{ background: "#FEF3F2", border: "1px solid #FDA29B", color: T.danger, fontSize: 14.5 }}
                  >
                    {submitError}
                  </div>
                )}

                <div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="rounded-md px-6 py-3.5"
                    style={{
                      ...headBtn,
                      background: submitting ? "#8A96A6" : T.navy,
                      color: T.paper,
                      cursor: submitting ? "not-allowed" : "pointer",
                    }}
                  >
                    {submitting ? "Submitting…" : "Submit Partnership Request"}
                  </button>
                </div>
              </form>
            )}
          </section>
        )}

        <footer className="mt-14 pb-4" style={{ color: T.muted, fontSize: 14, lineHeight: 1.6 }}>
          Questions? Email Jordan Lee at{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="pl-focus" style={{ color: T.navy, fontWeight: 600 }}>
            {CONTACT_EMAIL}
          </a>
          . PilotLink USA, Bakersfield, California.
        </footer>
      </main>
    </div>
  );
}
